/**
 * What Proctor and Wright are doing on the build item's own pull request.
 *
 * The review of a pull request has always been readable on the review case
 * file, one hop away, on a different work item. The person looking at the
 * build item is the person who has to decide whether the result is good, so
 * the sheet reads it here, in one GET:
 *
 *   `GET /api/code-review/work-items/:id/reviews` → `{ pr, reviews, fixRounds }`
 *
 * the same `CodeReviewPrReviewsResponse` the PR Reviews modal reads, addressed
 * by work item. It resolves the PR-state row server-side, so no id is guessed
 * here (`WorkItemReviewState` deliberately never exposes that row's id).
 *
 * What it returns:
 *
 *   - `report`: the newest POSTED report. Findings come only from it: a review
 *     that has not been posted is not a review, and a placeholder claiming one
 *     is worse than silence;
 *   - `inProgress`: the newest report still `in_progress`, so the sheet can say
 *     Proctor is reviewing, as in progress, never as a review;
 *   - `proctorStatus` and `fixRounds`: what Proctor is doing about the pull
 *     request, and where Wright's fix-round line stands on it.
 *
 * It stays fresh while something is moving. The review is posted by a run on
 * the REVIEW CASE, so the build item's own run events never see it land: the
 * read re-runs when either item's run moves or either item changes, and polls
 * as a backstop while Proctor is queued or reviewing or a fix round runs. It
 * stops polling once the status settles. A 404, a failed read, or no pull
 * request reads as nothing at all.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { captureUiError } from "../sentry.js";
import type {
  CodeReviewFixRoundStatus,
  CodeReviewPrReviewsResponse,
  CodeReviewReport,
  ProctorPrStatus,
  ReviewFinding,
  WorkItemReviewThread,
} from "@yagni/shared";
import { useRunLiveFlip } from "./useRunLiveFlip.js";
import { useWorkItemEntityChange } from "./useWorkItemEntityChange.js";
import { LIVE_RUN_POLL_MS } from "./useWorkItemRuns.js";

export interface PullRequestReview {
  /** The newest POSTED report, or null when nothing has been posted. */
  report: CodeReviewReport | null;
  /** The newest report still in progress, or null. Never a review: a fact that one is running. */
  inProgress: CodeReviewReport | null;
  /**
   * The findings the posted review actually DELIVERED: the refinement
   * survivors minus the ones verification refuted (YAG-419 — a refuted
   * survivor never posts, and a verdict upgraded to approve after a full
   * refutation (YAG-441) posted no accusations at all). Raw survivors print
   * here as posted findings they never were. A report with no refinement
   * record (older rows, markdown-only reviews) has nothing to subtract —
   * the survivors it never had print none, same as before.
   */
  deliveredFindings: ReviewFinding[];
  /**
   * Whether the PR record says its latest pass was a follow-up. Read off the
   * same response (`pr.followUp`), never guessed: "initial review" over a
   * third pass would be a wrong fact in the fact voice.
   */
  followUp: boolean;
  /** What Proctor is doing about the pull request; null until read, or when nothing is watched. */
  proctorStatus: ProctorPrStatus | null;
  /** The review case the pull request's reviews run on, as the PR record names it. */
  reviewCaseId: string | null;
  /** The pull request's record: its page on Pull request reviews is `/reviews/<id>`. */
  prStateId: string | null;
  /** Where the build item's fix-round line stands; null when unknown. */
  fixRounds: CodeReviewFixRoundStatus | null;
  /** True until the first read settles, so a section can hold rather than flash. */
  loading: boolean;
  /**
   * Every comment Proctor posted on the pull request, with its GitHub thread
   * (`GET /api/work-items/:id/review-threads`). Empty when the read failed or
   * the pull request predates the record: the findings above stand in.
   */
  threads: WorkItemReviewThread[];
}

/** Reports that represent a review a person could actually read on the PR. */
function newestPosted(reviews: CodeReviewReport[] | undefined): CodeReviewReport | null {
  if (!Array.isArray(reviews)) return null;
  return reviews.find((report) => report.status === "posted") ?? null;
}

/** The newest report, when it is still running. An older one stuck in progress is history. */
function runningNow(reviews: CodeReviewReport[] | undefined): CodeReviewReport | null {
  if (!Array.isArray(reviews)) return null;
  const newest = reviews[0];
  return newest?.status === "in_progress" ? newest : null;
}

/**
 * Survivors as they posted: model-refuted ones never posted, and a model's
 * `unverified` posted as a nit (`verification.ts` applyVerifications). A
 * failed verification loop passes through. The panel's fallback when the
 * pull request's own threads cannot be read, and the review card's counts.
 */
export function deliveredFindingsOf(report: CodeReviewReport | null): ReviewFinding[] {
  const decisions = report?.refinementDecisions;
  if (!decisions) return [];
  const verdicts = new Map(
    (decisions.verifications ?? [])
      .filter((v) => v.failed !== true)
      .map((v) => [v.id, v.verdict] as const),
  );
  return decisions.survivors
    .filter((finding) => verdicts.get(finding.id) !== "refuted")
    .map((finding) =>
      verdicts.get(finding.id) === "unverified" ? { ...finding, severity: "nit" } : finding,
    );
}

/** Proctor is lined up or reviewing, or Wright's round is running: worth a timer. */
export function reviewIsLive(
  proctorStatus: ProctorPrStatus | null,
  fixRounds: CodeReviewFixRoundStatus | null,
): boolean {
  return proctorStatus === "queued" || proctorStatus === "reviewing" || fixRounds?.running === true;
}

export function usePullRequestReview(
  workItemId: string,
  /** False on an item with no pull request: no PR, no request. */
  enabled: boolean,
  /** The review case folded under this item, when the list carries it. */
  reviewCaseItemId: string | null = null,
): PullRequestReview {
  const [body, setBody] = useState<CodeReviewPrReviewsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [threads, setThreads] = useState<WorkItemReviewThread[]>([]);
  const [nonce, setNonce] = useState(0);
  const reread = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setBody(null);
      setLoading(false);
      setThreads([]);
      return;
    }
    let cancelled = false;
    if (nonce === 0) setLoading(true);
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/review-threads`, {
      credentials: "include",
    })
      .then((res) => (res.ok ? (res.json() as Promise<{ threads?: unknown }>) : null))
      .then((next) => {
        if (cancelled) return;
        setThreads(Array.isArray(next?.threads) ? (next.threads as WorkItemReviewThread[]) : []);
      })
      .catch((err: unknown) => {
        // The newest review's findings stand in; the page still says something
        // true. Reported on the first read only, as the reviews read is.
        if (nonce === 0) {
          captureUiError(err, {
            area: "Work",
            operation: "read_review_threads",
            severity: "warning",
          });
        }
        if (!cancelled && nonce === 0) setThreads([]);
      });
    fetch(`/api/code-review/work-items/${encodeURIComponent(workItemId)}/reviews`, {
      credentials: "include",
    })
      .then((res) => {
        // A 404 is the ordinary answer: no watched pull request. Any other
        // non-2xx is a broken read, and goes the failed-read way below.
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`pull request reviews read failed (${res.status})`);
        return res.json() as Promise<CodeReviewPrReviewsResponse>;
      })
      .then((next) => {
        if (!cancelled) setBody(next && typeof next === "object" ? next : null);
      })
      .catch((err: unknown) => {
        // A failed first read is not a review: say nothing on the page, and
        // report it once. A failed re-read keeps what the last good one
        // showed; a blip must not blank it, nor page every 15 seconds.
        if (nonce === 0) {
          captureUiError(err, { area: "Work", operation: "read_pr_reviews", severity: "warning" });
        }
        if (!cancelled && nonce === 0) setBody(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId, enabled, nonce]);

  const proctorStatus = body?.pr?.proctorStatus ?? null;
  const fixRounds = body?.fixRounds ?? null;
  const reviewCaseId = reviewCaseItemId ?? body?.pr?.workItemId ?? null;

  // The review posts from a run on the review case; a fix round runs on this
  // item. Either moving, or either item changing, is a reason to read again.
  useRunLiveFlip(enabled ? reviewCaseId : null, reread);
  useRunLiveFlip(enabled ? workItemId : null, reread);
  useWorkItemEntityChange(enabled ? reviewCaseId : null, reread);
  useWorkItemEntityChange(enabled ? workItemId : null, reread);

  // A dropped frame must not leave "reviewing" on the page after it posted.
  const live = enabled && reviewIsLive(proctorStatus, fixRounds);
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(reread, LIVE_RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [live, reread]);

  const report = useMemo(() => newestPosted(body?.reviews), [body]);
  const deliveredFindings = useMemo(() => deliveredFindingsOf(report), [report]);
  return {
    report,
    inProgress: runningNow(body?.reviews),
    deliveredFindings,
    followUp: body?.pr?.followUp === true,
    proctorStatus,
    reviewCaseId,
    prStateId: body?.pr?.prStateId ?? null,
    fixRounds,
    loading,
    threads,
  };
}
